import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { memo } from 'react';

import isSafeUrl from '@/shared/utils/urlHelpers';
import fallbackLogo from '@/assets/img/logos/png/logoSquare.png';
import './DataProductTile.css';

const DataProductTile = memo(function DataProductTile({
    title,
    description,
    metadata,
    detailUrl,
    featured = false,
    image,
    primaryCTA,
    secondaryCTA,
    onChatClick,
    previewUrl,
    embeddable = false,
    previewImage = null,
}) {
    const showFeaturedElements = featured && image;
    const showIframePreview = !showFeaturedElements && previewUrl && embeddable;
    const showImagePreview =
        !showFeaturedElements && !showIframePreview && previewImage;
    const showFallback =
        !showFeaturedElements && !showIframePreview && !showImagePreview;

    return (
        <div className={`data-product-tile ${featured ? 'featured' : ''}`}>
            {showFeaturedElements && (
                <>
                    <div className="data-product-featured-badge">Featured</div>
                    <div className="data-product-image-wrapper mb-3">
                        <img
                            src={image}
                            alt={title}
                            className="data-product-image img-fluid"
                            draggable="false"
                        />
                    </div>
                </>
            )}

            {showIframePreview && (
                <div className="data-product-preview-area mb-2">
                    <iframe
                        src={previewUrl}
                        title={`Preview of ${title}`}
                        className="data-product-preview-iframe"
                        loading="lazy"
                        sandbox="allow-scripts allow-same-origin"
                        tabIndex={-1}
                        aria-hidden="true"
                    />
                    <div className="data-product-preview-fade" />
                </div>
            )}

            {showImagePreview && (
                <div className="data-product-preview-area mb-2">
                    <img
                        src={previewImage}
                        alt={`Preview of ${title}`}
                        className="data-product-preview-img"
                        loading="lazy"
                        draggable="false"
                    />
                    <div className="data-product-preview-fade" />
                </div>
            )}

            {showFallback && (
                <div className="data-product-preview-area data-product-fallback mb-2">
                    <img
                        src={fallbackLogo}
                        alt="Sustainability Hub"
                        className="data-product-fallback-logo"
                        draggable="false"
                    />
                </div>
            )}

            <div className="data-product-content">
                {detailUrl ? (
                    <Link
                        to={detailUrl}
                        className="text-decoration-none text-reset"
                    >
                        <h3 className="data-product-title fw-semibold mb-3">
                            {title}
                        </h3>
                    </Link>
                ) : (
                    <h3 className="data-product-title fw-semibold mb-3">
                        {title}
                    </h3>
                )}
                <p className="data-product-description mb-2">{description}</p>
                <p className="data-product-metadata">{metadata}</p>
            </div>

            <div className="data-product-cta-group">
                <Button
                    className="data-product-primary-btn"
                    onClick={() =>
                        isSafeUrl(primaryCTA.link) &&
                        (window.location.href = primaryCTA.link)
                    }
                    aria-label={`${primaryCTA.text} - ${title}`}
                >
                    {primaryCTA.text}
                </Button>
                <Button
                    className="data-product-secondary-btn"
                    onClick={() => {
                        if (onChatClick) {
                            onChatClick();
                        } else if (isSafeUrl(secondaryCTA.link)) {
                            window.location.href = secondaryCTA.link;
                        }
                    }}
                    aria-label={`${secondaryCTA.text} - ${title}`}
                >
                    {secondaryCTA.text}
                </Button>
                {detailUrl && (
                    <Link
                        to={detailUrl}
                        className="btn btn-outline-dark btn-sm w-100 mt-1"
                    >
                        Learn more
                    </Link>
                )}
            </div>
        </div>
    );
});

DataProductTile.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    metadata: PropTypes.string.isRequired,
    detailUrl: PropTypes.string,
    featured: PropTypes.bool,
    image: PropTypes.string,
    primaryCTA: PropTypes.shape({
        text: PropTypes.string.isRequired,
        link: PropTypes.string,
    }).isRequired,
    secondaryCTA: PropTypes.shape({
        text: PropTypes.string.isRequired,
        link: PropTypes.string,
    }).isRequired,
    onChatClick: PropTypes.func,
    previewUrl: PropTypes.string,
    embeddable: PropTypes.bool,
    previewImage: PropTypes.string,
};

export default DataProductTile;
